<script lang="ts" setup>
import { ContractTemplateFieldName } from '@/core/collections/contracts-api/contract-template-field-names/contract-template-field-name.interface';
import { ContractTemplateField } from '@/core/collections/contracts-api/customers/contract-templates/contract-template.interface';
import FormElement from '@/core/components/form-elements/FormElement.vue';
import { useStore } from '@/core/hooks/useStore';
import { NxtLink } from '@nxtpeople/nxt-styles';
import { computed, onMounted, PropType, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ContractTemplateCalculatedValuesForm from './ContractTemplateCalculatedValuesForm.vue';
import ContractTemplateTags from './ContractTemplateTags.vue';
import { useContractTemplate } from './useContractTemplate';
import { useContractTemplateKeys } from './useContractTemplateKeys';

const { t } = useI18n();
const { dispatch } = useStore();
const { COMPANY_KEYS } = useContractTemplateKeys();
const contractTemplate = useContractTemplate();

//#region Props
const emits = defineEmits<{ (e: 'onUpdateField', payload: { fieldNameId: number; key: string; value: any }): void }>();
const props = defineProps({
   field: { type: Object as PropType<ContractTemplateField>, default: null },
   fieldName: { type: Object as PropType<ContractTemplateFieldName>, default: null },
   disabled: { type: Boolean, default: false },
   errors: { type: Object, default: () => ({}) },
   customOptionsUrl: { type: String, default: null },
   hideCalculatedValues: { type: Boolean, default: false },
   mode: { type: String as PropType<'light' | 'default'>, default: 'light' },
});
const { field, fieldName, customOptionsUrl } = toRefs(props);
//#endregion

//#region Dropdown
const options = ref<Array<any>>([]);
const selectedValue = computed(() => field.value?.selected_value);

watch(selectedValue, setSelectedValue);
function setSelectedValue(selectedValue: any) {
   if (!fieldName.value.optionsUrl) return;
   if (fieldName.value.options?.length) return;
   options.value = [selectedValue];
}

onMounted(async () => {
   if (selectedValue.value) {
      options.value = [selectedValue.value];
   }

   if (fieldName.value.form_type === 'dropdown') {
      const isCompanyKey = COMPANY_KEYS.includes(fieldName.value.key);
      if (!isCompanyKey && fieldName.value.optionsUrl) {
         const response = await dispatch(fieldName.value.optionsUrl);
         options.value = Array.isArray(response) ? response : (response?.data ?? []);
         return;
      }
   }

   if (fieldName.value.options?.length) {
      return (options.value = fieldName.value.options);
   }
});

const fieldNameOptions = computed(() => fieldName.value.options);

watch(fieldNameOptions, setFieldNameOptions, { immediate: true });
function setFieldNameOptions(newOptions?: Array<any>) {
   if (!newOptions) return;
   options.value = [...newOptions];
}
//#endregion

//#region ContractTemplateCalculatedValueForm
const showContractTemplateCalculatedValueForm = ref(false);

function saveContractTemplateCalculatedValues(field: ContractTemplateField) {
   const fieldIndex = contractTemplate.value.contract_template_fields.findIndex((ctf) => ctf.field_key === field.field_key);
   if (fieldIndex > -1) {
      contractTemplate.value.contract_template_fields.splice(fieldIndex, 1, field);
   } else {
      contractTemplate.value.contract_template_fields.push({
         ...field,
         contract_template_field_name: fieldName.value,
         contract_template_field_name_id: fieldName.value.id,
         is_fixed: false,
         is_required: fieldName.value.is_required || false,
         field_key: fieldName.value.key,
      });
   }
   showContractTemplateCalculatedValueForm.value = false;
}

//#endregion

//#region Calculated Values
const calculatedValues = computed(() => field.value?.calculated_values || []);
//#endregion

//#region
function changeModelValue(value: any) {
   if (!value) {
      emits('onUpdateField', { fieldNameId: fieldName.value.id, key: 'selected_value', value });
   }
   if (typeof value === 'object') {
      emits('onUpdateField', { fieldNameId: fieldName.value.id, key: 'selected_value', value });
      emits('onUpdateField', { fieldNameId: fieldName.value.id, key: 'value', value: value?.id });
   } else {
      emits('onUpdateField', { fieldNameId: fieldName.value.id, key: 'value', value });
   }
}
//#endregion

//#region fix field if has calculated values
const hasCalculatedValues = computed(() => !!field.value?.calculated_values?.length);

function fixFieldIfHasCalculatedValues(hasCalculatedValues: boolean) {
   if (hasCalculatedValues) {
      emits('onUpdateField', { fieldNameId: fieldName.value.id, key: 'is_fixed', value: true });
   }
}

watch(hasCalculatedValues, fixFieldIfHasCalculatedValues, { immediate: true });
//#endregion
</script>

<template>
   <div :class="['contract-template-wizard-field', `-${fieldName.key}`, { '-hide-calculated-values': hideCalculatedValues }]">
      <div class="contract-template-wizard-field__input">
         <FormElement
            :model-value="field?.value"
            :label="fieldName.label"
            :disabled="disabled"
            :errors="errors"
            :form-type="fieldName.form_type"
            :name="fieldName.key"
            :mode
            :display-key="fieldName.display_key"
            :options="options"
            :options-url="customOptionsUrl || fieldName.optionsUrl"
            :custom-display-template="fieldName.customDisplayTemplate"
            :required="!!fieldName.is_required"
            :emit-object="true"
            :dropdown-params="fieldName.dropdownParams"
            @update:model-value="changeModelValue"
         />
      </div>

      <div>
         <button
            :class="['contract-template-wizard-field__toggle', 'btn', 'btn-icon', { '-active': field?.is_fixed }]"
            :button-type="field?.is_fixed ? 'primary' : 'grey'"
            :title="field?.is_fixed ? t('generic.feedback.set-not-fixed') : t('generic.feedback.set-fixed')"
            :disabled="disabled || !!field?.calculated_values?.length"
            @click="emits('onUpdateField', { fieldNameId: fieldName.id, key: 'is_fixed', value: !field?.is_fixed })"
         >
            <i v-if="field?.is_fixed" class="fa-solid fa-lock"></i>
            <i v-else class="fa-solid fa-lock-open"></i>
         </button>
      </div>

      <div>
         <button
            v-if="!fieldName.is_required"
            :class="['contract-template-wizard-field__toggle', 'btn', 'btn-icon', { '-active': field?.is_required }]"
            :title="field?.is_required ? t('generic.feedback.make-optional') : t('generic.feedback.make-required')"
            :disabled="disabled"
            @click="emits('onUpdateField', { fieldNameId: fieldName.id, key: 'is_required', value: !field?.is_required })"
         >
            <i style="font-size: 0.9rem; margin-top: 0.15rem" class="fa-solid fa-star-of-life"></i>
         </button>
      </div>

      <!-- Calculated Values -->
      <div v-if="!hideCalculatedValues" class="contract-template-wizard-field__conditions">
         <NxtLink
            v-if="(field?.calculated_values || []).length <= 0"
            class="text-start"
            :disabled="disabled"
            @on-click="showContractTemplateCalculatedValueForm = true"
         >
            <span>+ {{ t('actions.add-type', { name: t('companies.templates.calculated-value', 1) })?.toLowerCase() }}</span>
         </NxtLink>

         <ContractTemplateTags
            v-else
            :calculated-values="calculatedValues"
            :field="field"
            :mode
            :field-name="fieldName"
            :options="options"
            @open-calculated-fields-modal="showContractTemplateCalculatedValueForm = true"
         />
      </div>

      <ContractTemplateCalculatedValuesForm
         v-if="showContractTemplateCalculatedValueForm"
         :loading="disabled"
         :field="field"
         :mode
         :field-name="fieldName"
         @on-cancel="showContractTemplateCalculatedValueForm = false"
         @on-save="saveContractTemplateCalculatedValues"
      />
   </div>
</template>

<style lang="scss">
.contract-template-wizard-field {
   display: grid;
   grid-template-columns: 1fr 2.77rem 2.77rem;
   grid-template-rows: 1fr auto;
   gap: var(--nxt-gutter-small);

   grid-template-areas:
      '. . .'
      'conditions conditions conditions';

   &.-color {
      .contract-template-wizard-field__toggle {
         margin-top: 0;
      }
   }

   &.-hide-calculated-values {
      display: flex;
   }
}
.contract-template-wizard-field__conditions {
   grid-area: conditions;
}

.contract-template-wizard-field__input {
   width: 100%;
}
.contract-template-wizard-field__toggle {
   margin-top: 2.2rem;
   padding-left: 0;
   padding-right: 0;
   height: toRem(36);
   min-width: toRem(36);
   display: flex;
   align-items: center;
   justify-content: center;

   &.-active {
      color: var(--nxt-brand-primary-light);
   }
}
</style>
